import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getRounds, getTournament, getPlayers, ApiError } from '@/lib/api';
import type { Round, Tournament, Player } from '@swiss-magic/shared-types';
import { showErrorToast } from '@/lib/toast';

export function useRoundsData(tournamentId: string | undefined) {
  const { t } = useTranslation('director');
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [rounds, setRounds] = useState<Round[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRound, setSelectedRound] = useState<number>(0);
  // Story 010 polling fix: only auto-jump to the latest round on the
  // first load for this tournament. Subsequent refreshes (every 5s
  // while polling) must preserve whatever round the director is
  // currently viewing — otherwise the page snaps back every poll
  // tick.
  const hasInitializedSelectedRoundRef = useRef(false);

  // Reset the "first load" flag whenever the tournament changes so
  // navigating between tournaments still defaults to the latest round.
  useEffect(() => {
    hasInitializedSelectedRoundRef.current = false;
  }, [tournamentId]);

  const loadData = useCallback(async () => {
    if (!tournamentId) return;
    try {
      const [t, r, { players: p }] = await Promise.all([
        getTournament(tournamentId),
        getRounds(tournamentId),
        getPlayers(tournamentId),
      ]);
      setTournament(t);
      setRounds(r);
      setPlayers(p.filter((pl) => pl.status === 'accepted'));
      if (r.length > 0 && !hasInitializedSelectedRoundRef.current) {
        setSelectedRound(r.length - 1);
        hasInitializedSelectedRoundRef.current = true;
      }
    } catch (err: unknown) {
      const msg =
        err instanceof ApiError ? err.userMessage : t('rounds_page.toast.load_failed');
      showErrorToast(msg);
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Story 010 — poll every 5s while there's at least one round that
  // isn't fully confirmed, so player self-reports surface on the
  // director's pairings page without a manual refresh. Visibility-aware
  // (pause when the tab is hidden) so backgrounded windows don't waste
  // requests. The poll calls loadData which already refreshes the local
  // state in a single write — no extra plumbing.
  useEffect(() => {
    if (!tournamentId) return;
    const POLL_MS = 5000;

    const stillNeedsPolling = () => {
      // Poll when at least one round is published with at least one
      // non-confirmed pairing. Round-level status='completed' means all
      // results were confirmed and the round was closed — no point
      // polling for new self-reports there.
      return rounds.some(
        (r) =>
          r.status === 'published' &&
          r.pairings.some(
            (p) => p.blackPlayer && p.resultStatus !== 'confirmed',
          ),
      );
    };

    let interval: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (interval !== null) return;
      if (!stillNeedsPolling()) return;
      interval = setInterval(() => {
        if (!stillNeedsPolling()) {
          if (interval) clearInterval(interval);
          interval = null;
          return;
        }
        if (document.visibilityState === 'visible') {
          loadData();
        }
      }, POLL_MS);
    };
    const stop = () => {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadData();
        start();
      } else {
        stop();
      }
    };

    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, [tournamentId, rounds, loadData]);

  return {
    tournament,
    setTournament,
    rounds,
    setRounds,
    players,
    loading,
    selectedRound,
    setSelectedRound,
    loadData,
  };
}
